import { AsyncProcessingStatus, AsyncProcessingType, CustomColumnTypes } from "@/enum/list.enum";

import type { ListPerson } from "../../../../../schema/list";

export type AiSnippetStatus = "not_created" | "creating" | "created";

/** Matches AI snippet placeholders like `{column_<id>_<name>}`; captures the numeric column id. */
const COLUMN_PLACEHOLDER_RE = /\{column_(\d+)/g;

/** Minimal shapes we read off the loosely-typed campaign nodes / list column configs. */
type CampaignNode = { data?: unknown };
type ListColumn = { id?: number; type: CustomColumnTypes };

/**
 * Returns the ids of AI_SNIPPET columns referenced by a campaign's message templates.
 * Scans every node's `data` for `{column_<id>_…}` placeholders, then keeps only the ids
 * that resolve to an AI_SNIPPET column on the list.
 */
export const getCampaignAiSnippetColumnIds = (
	initialNodes: CampaignNode[] | undefined | null,
	listColumns: ListColumn[] | undefined | null
): number[] => {
	if (!initialNodes?.length || !listColumns?.length) {
		return [];
	}

	const referencedIds = new Set<number>();

	for (const node of initialNodes) {
		if (!node?.data) {
			continue;
		}

		const haystack = JSON.stringify(node.data);

		for (const match of haystack.matchAll(COLUMN_PLACEHOLDER_RE)) {
			referencedIds.add(Number(match[1]));
		}
	}

	return [...referencedIds].filter((id) =>
		listColumns.some((column) => column.id === id && column.type === CustomColumnTypes.AI_SNIPPET)
	);
};

/** Unwraps a column value (AI snippet values are stored as `{ answer }`) and checks it is non-empty. */
const hasGeneratedValue = (value: unknown): boolean => {
	const unwrapped =
		value && typeof value === "object" && "answer" in value
			? (value as { answer?: unknown }).answer
			: value;

	return unwrapped !== null && unwrapped !== undefined && unwrapped !== "";
};

/**
 * Derives the AI snippet generation status for a lead, scoped to the AI_SNIPPET columns the
 * assigned campaign actually references. Returns `null` when the campaign uses no AI snippets.
 *
 * - `creating`     — the lead is currently being processed for AI snippets (PENDING / PROCESSING).
 * - `created`      — every referenced AI snippet column has a generated value.
 * - `not_created`  — otherwise (incl. FAILED, which still needs (re)generation).
 */
export const getAiSnippetStatus = (
	row: ListPerson,
	aiSnippetColumnIds: number[]
): AiSnippetStatus | null => {
	if (aiSnippetColumnIds.length === 0) {
		return null;
	}

	const isProcessing =
		row.asyncProcessingType === AsyncProcessingType.AI_SNIPPET &&
		(row.asyncProcessingStatus === AsyncProcessingStatus.PENDING ||
			row.asyncProcessingStatus === AsyncProcessingStatus.PROCESSING);

	if (isProcessing) {
		return "creating";
	}

	const allGenerated = aiSnippetColumnIds.every((id) =>
		hasGeneratedValue(row.columns?.find((column) => column.columnConfigId === id)?.value)
	);

	return allGenerated ? "created" : "not_created";
};
